<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useStore } from 'dashboard/composables/store';
import OffersAPI from 'dashboard/api/offers';
import VoipDateTimePicker from './VoipDateTimePicker.vue';
import { toLocalDateTime, toUtcDateTime } from '../helpers/localDateTime';

// Form puro de finalização — sem chrome de modal. Usado em:
//   - CallDispositionModal.vue (envolto em overlay, Commit 1 aceita pending)
//   - VoipCallReview.vue (embed inline na detail pane — sempre tem callRecord)
//
// Props:
//   call-record: { id, disposition, notes, ... } — nullable a partir de Commit 1
//   pending-call-end: { provider_call_id, peer_phone, ... } — usado quando
//     callRecord é null (chamada terminou, webhook ainda nao chegou).
//   show-transcription-panel: boolean (modal usa true; review false porque já tem painel próprio)
// Emits:
//   saved: payload — após save bem-sucedido
//   dismissed: () — usuário clicou "preencher depois" (modal only)

const props = defineProps({
  callRecord: { type: Object, default: null },
  pendingCallEnd: { type: Object, default: null },
  showTranscriptionPanel: { type: Boolean, default: false },
  showDismiss: { type: Boolean, default: false },
  draftCache: { type: Map, default: () => new Map() },
});
const emit = defineEmits(['saved', 'dismissed']);

const store = useStore();
const { isWorkspaceV2 } = useWorkspaceV2();
const formId = useId();
const route = useRoute();
const { t } = useI18n();
const saving = ref(false);
let lifecycleId = 0;
const saveContext = () => `${route.params.accountId}-${props.callRecord?.id}-${props.pendingCallEnd?.provider_call_id}-${lifecycleId}`;
const config  = computed(() => store.getters['voipDisposition/getConfig']);

const form = ref({
  disposition: '',
  notes: '',
  next_contact_at: '',
  appointment_date: '',
  lead_temperature: '',
  disposition_custom: {},
  // Phase 2.9.10 c2: Valor do negócio (modo livre digita centavos,
  // modo offer grava offer.id + offer.value_cents).
  business_value_cents: null,
  linked_offer_id: null,
});
const errors = ref({});

// Phase 2.9.10 c2: catalogo de offers (carregado on-demand quando o campo
// business_value tiver mode=offer). Falha silenciosa — UI cai em lista
// vazia e agente pode só digitar livre.
const offers = ref([]);

let formBaseline = JSON.stringify(form.value);
const callDrafts = props.draftCache;
let draftAccountId = route.params.accountId;

watch(() => props.callRecord, (newRecord, previousRecord) => {
  const accountChanged = draftAccountId !== route.params.accountId;
  if (accountChanged) {
    callDrafts.clear();
    draftAccountId = route.params.accountId;
    previousRecord = null;
  }
  const arrivingPendingCall = !accountChanged && !previousRecord && newRecord?.provider_call_id &&
    newRecord.provider_call_id === props.pendingCallEnd?.provider_call_id;
  const sameCall = arrivingPendingCall || (newRecord?.id != null && newRecord.id === previousRecord?.id);
  if (!sameCall) {
    lifecycleId += 1;
    if (previousRecord?.id != null) {
      const values = JSON.stringify(form.value);
      if (values !== formBaseline) callDrafts.set(previousRecord.id, { values, baseline: formBaseline });
      else callDrafts.delete(previousRecord.id);
    }
    const draft = callDrafts.get(newRecord?.id);
    if (draft) {
      form.value = JSON.parse(draft.values);
      formBaseline = draft.baseline;
      errors.value = {};
      return;
    }
  }
  if (sameCall && (saving.value || JSON.stringify(form.value) !== formBaseline)) return;
  if (!newRecord) {
    // Sem CallRecord ainda — form comeca vazio. Nao reseta se o usuario
    // ja digitou algo (watch so reage quando callRecord fica !== anterior).
    return;
  }
  form.value = {
    disposition:        newRecord.disposition || '',
    notes:              newRecord.notes || '',
    next_contact_at:    toLocalDateTime(newRecord.next_contact_at),
    appointment_date:   toLocalDateTime(newRecord.appointment_date),
    lead_temperature:   newRecord.lead_temperature || '',
    disposition_custom: newRecord.disposition_custom ? { ...newRecord.disposition_custom } : {},
    // Phase 2.9.10 c2
    business_value_cents: newRecord.business_value_cents ?? null,
    linked_offer_id:      newRecord.linked_offer_id ?? null,
  };
  formBaseline = JSON.stringify(form.value);
  errors.value = {};
}, { immediate: true });

const offersLoading = ref(false);
const offersError = ref(false);
let offersRequestId = 0;
async function loadOffers() {
  const cfg = config.value?.business_value_config;
  if (!cfg?.enabled || cfg.mode !== 'offer' || offersLoading.value) return;
  const requestId = ++offersRequestId;
  const accountId = route.params.accountId;
  const isCurrent = () => requestId === offersRequestId && accountId === route.params.accountId;
  offersLoading.value = true;
  offersError.value = false;
  try {
    const { data } = await OffersAPI.getOffers();
    if (!isCurrent()) return;
    const all = Array.isArray(data) ? data : (data?.offers || data?.data || []);
    offers.value = cfg.linked_offer_group_id
      ? all.filter(o => o.offer_group_id === cfg.linked_offer_group_id)
      : all;
  } catch {
    if (isCurrent()) offersError.value = true;
  } finally {
    if (isCurrent()) offersLoading.value = false;
  }
}
watch([() => config.value?.business_value_config, () => route.params.accountId], () => {
  offersRequestId += 1;
  offersLoading.value = false;
  offersError.value = false;
  offers.value = [];
  loadOffers();
}, { immediate: true });
onBeforeUnmount(() => { offersRequestId += 1; });

const isEnabled  = field => !!config.value?.enabled_fields?.[field];
const isRequired = field => !!config.value?.required_fields?.[field];
// Spec 2026-04-23: options agora e array de hashes { name, funnel_id,
// funnel_stage }. UI do modal so precisa dos nomes no dropdown; backend
// recebe `disposition: name` como sempre.
const options    = computed(() => {
  const raw = config.value?.options || [];
  return raw.map(o => (typeof o === 'string' ? { name: o } : (o || {}))).filter(o => o.name);
});
const optionNames = computed(() => options.value.map(o => o.name));
const customFields = computed(() => config.value?.custom_fields || []);
const temperatureLevels = computed(() => config.value?.constants?.lead_temperatures || []);

// Phase 2.9.10 c2
const businessValueConfig = computed(() => config.value?.business_value_config || { enabled: false, mode: 'free' });
const businessValueEnabled = computed(() => !!businessValueConfig.value.enabled);
const businessValueRequired = computed(() => !!businessValueConfig.value.required);
const businessValueMode = computed(() => businessValueConfig.value.mode || 'free');

// Helper que converte "1.234,56" ou "1234.56" em centavos (integer).
function brlToCents(str) {
  if (str === null || str === '') return null;
  const normalized = String(str).replace(/\./g, '').replace(',', '.');
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? Math.round(num * 100) : null;
}
function centsToBrl(cents) {
  if (cents == null) return '';
  return (cents / 100).toFixed(2).replace('.', ',');
}

// Wrapper reativo pro input de valor livre — usuario digita em BRL,
// armazenamos em centavos.
const businessValueInput = computed({
  get: () => centsToBrl(form.value.business_value_cents),
  set: (v) => { form.value.business_value_cents = brlToCents(v); }
});

// Quando seleciona oferta do catalogo, copia o valor dela pro cents.
function onOfferSelected(offerId) {
  const id = offerId ? parseInt(offerId, 10) : null;
  form.value.linked_offer_id = id;
  if (id) {
    const offer = offers.value.find(o => o.id === id);
    if (offer) {
      // offer.value vem do backend em unidade inteira (reais), converte
      form.value.business_value_cents = Math.round(parseFloat(offer.value || 0) * 100);
    }
  } else {
    form.value.business_value_cents = null;
  }
}
const showStaleDispositionWarning = computed(() => {
  const current = form.value.disposition;
  return current && !optionNames.value.includes(current);
});

const isAlreadyFilled = computed(() => !!props.callRecord?.disposition_filled_at);
const lastSavedLabel = computed(() => {
  if (!props.callRecord?.disposition_filled_at) return null;
  const dt = new Date(props.callRecord.disposition_filled_at);
  return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
});

// Phase 2.5 A: atalhos de preenchimento rapido.
// Presets vem da config; cada um define quais campos preenche.
// Aplicacao: clique no botao OU pressionar tecla 1-9 (so quando form ta focado).
const presets = computed(() => config.value?.presets || []);

// Resolve offset strings como "+1d@09:00" / "today@18:00" pra ISO
// que VoipDateTimePicker aceita (YYYY-MM-DDTHH:MM).
function resolveOffset(offsetStr) {
  if (!offsetStr) return '';
  const match = offsetStr.match(/^(today|\+(\d+)d)(?:@(\d{2}):(\d{2}))?$/);
  if (!match) return '';
  const d = new Date();
  if (match[2]) d.setDate(d.getDate() + parseInt(match[2], 10));
  // today mantem hoje
  const h = parseInt(match[3] || '09', 10);
  const m = parseInt(match[4] || '00', 10);
  d.setHours(h, m, 0, 0);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(h).padStart(2, '0');
  const mi = String(m).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function applyPreset(preset) {
  if (!preset?.fields) return;
  const f = preset.fields;
  if (f.disposition)      form.value.disposition      = f.disposition;
  if (f.notes)            form.value.notes            = f.notes;
  if (f.lead_temperature) form.value.lead_temperature = f.lead_temperature;
  if (f.next_contact_at_offset)   form.value.next_contact_at  = resolveOffset(f.next_contact_at_offset);
  if (f.appointment_date_offset)  form.value.appointment_date = resolveOffset(f.appointment_date_offset);
  if (f.custom && typeof f.custom === 'object') {
    form.value.disposition_custom = { ...form.value.disposition_custom, ...f.custom };
  }
}

// Listener de tecla: ativa quando tecla 1-9 pressionada e o form esta visivel.
// Ignora se target eh input/textarea/select pra nao atrapalhar digitacao normal.
function onKeydown(e) {
  if (!/^[1-9]$/.test(e.key)) return;
  const tag = (e.target?.tagName || '').toLowerCase();
  if (['input', 'textarea', 'select'].includes(tag)) return;
  const preset = presets.value.find(p => p.hotkey === e.key);
  if (preset) { applyPreset(preset); e.preventDefault(); }
}
onMounted(() => document.addEventListener('keydown', onKeydown));
function hasUnsavedChanges() {
  return JSON.stringify(form.value) !== formBaseline ||
    Array.from(callDrafts.keys()).some(id => id !== props.callRecord?.id);
}
function retainCurrentDraft() {
  const id = props.callRecord?.id;
  if (id == null || draftAccountId !== route.params.accountId) return;
  const values = JSON.stringify(form.value);
  if (values !== formBaseline) callDrafts.set(id, { values, baseline: formBaseline });
  else callDrafts.delete(id);
}
defineExpose({ hasUnsavedChanges, saving });
onBeforeUnmount(() => {
  retainCurrentDraft();
  lifecycleId += 1;
  document.removeEventListener('keydown', onKeydown);
});

async function handleSubmit() {
  if (saving.value) return;
  const context = saveContext();
  saving.value = true;
  errors.value = {};
  try {
    const payload = {
      disposition:        form.value.disposition || null,
      notes:              form.value.notes || null,
      next_contact_at:    toUtcDateTime(form.value.next_contact_at),
      appointment_date:   toUtcDateTime(form.value.appointment_date),
      lead_temperature:   form.value.lead_temperature || null,
      disposition_custom: form.value.disposition_custom,
      // Phase 2.9.10 c2
      business_value_cents: form.value.business_value_cents,
      linked_offer_id:      form.value.linked_offer_id,
    };

    let result;
    // Phase 2.9.10 Commit 1: quando usado no modal pos-chamada, SEMPRE passa
    // pelo endpoint novo que lida com CallRecord ausente. Quando embed inline
    // no Review (sempre tem callRecord filled), pode usar o PATCH legacy.
    if (props.pendingCallEnd || !props.callRecord?.id) {
      result = await store.dispatch('voipDisposition/saveDispositionFromModal', {
        pendingCallEnd: props.pendingCallEnd,
        callRecord:     props.callRecord,
        payload,
      });
    } else {
      result = await store.dispatch('voipDisposition/saveDisposition', {
        callRecordId: props.callRecord.id,
        payload,
      });
    }
    if (context === saveContext()) {
      formBaseline = JSON.stringify(form.value);
      callDrafts.delete(props.callRecord?.id);
      emit('saved', result);
    }
  } catch (e) {
    if (context === saveContext()) {
      const validationErrors = e?.response?.data?.errors;
      errors.value = {
        ...(validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors) ? validationErrors : {}),
        base: validationErrors?.base || t('VOIP_DEVICES.OVERVIEW.SAVE_REVIEW_ERROR'),
      };
    }
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <fieldset :disabled="saving" :class="{ '[&_input:not([type=checkbox])]:min-h-11 [&_select]:min-h-11 [&_button]:min-h-11 [&_input]:text-base [&_select]:text-base [&_textarea]:text-base [&_input:focus-visible]:outline [&_input:focus-visible]:outline-2 [&_input:focus-visible]:outline-n-brand [&_select:focus-visible]:outline [&_select:focus-visible]:outline-2 [&_select:focus-visible]:outline-n-brand [&_textarea:focus-visible]:outline [&_textarea:focus-visible]:outline-2 [&_textarea:focus-visible]:outline-n-brand': isWorkspaceV2 }" class="space-y-4 min-w-0 m-0 border-0 p-0">
    <p v-if="isAlreadyFilled" class="text-xs text-n-slate-11 italic">
      ✓ Última edição: {{ lastSavedLabel }}
    </p>

    <!-- Phase 2.5 A: Atalhos (presets) -->
    <div v-if="presets.length" class="rounded-xl border border-n-brand/30 bg-n-brand/5 p-2">
      <p class="text-[10px] font-medium text-n-slate-11 uppercase mb-1.5 px-1">Atalhos · aperte a tecla ou clique</p>
      <div class="flex flex-wrap gap-1.5">
        <button v-for="preset in presets" :key="preset.hotkey || preset.label" type="button"
                class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-n-solid-1 border border-n-weak hover:border-n-brand hover:bg-n-brand/10 text-sm text-n-slate-12 transition-colors"
                :title="`Preenche: ${preset.fields?.disposition || '—'} · ${preset.fields?.lead_temperature || ''}`"
                @click="applyPreset(preset)">
          <kbd v-if="preset.hotkey" class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-mono font-bold rounded bg-n-alpha-2 border border-n-weak">{{ preset.hotkey }}</kbd>
          <span class="text-base leading-none">{{ preset.icon || '⚡' }}</span>
          <span class="font-medium">{{ preset.label }}</span>
        </button>
      </div>
    </div>

    <!-- Disposition (sempre obrigatório config-default) -->
    <div>
      <label :for="`${formId}-disposition`" class="block text-sm font-medium text-n-slate-12 mb-1">
        Resultado
        <span v-if="isRequired('disposition')" class="text-n-ruby-9">*</span>
      </label>
      <select :id="`${formId}-disposition`" :aria-required="isRequired('disposition')" :aria-invalid="!!errors.disposition" v-model="form.disposition"
              class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12">
        <option value="">— Selecione —</option>
        <option v-for="opt in options" :key="opt.name" :value="opt.name">{{ opt.name }}</option>
        <option v-if="showStaleDispositionWarning" :value="form.disposition" disabled>
          {{ form.disposition }} (opção removida)
        </option>
      </select>
      <p v-if="errors.disposition" class="text-xs text-n-ruby-9 mt-1">{{ errors.disposition }}</p>
    </div>

    <div v-if="isEnabled('notes')">
      <label :for="`${formId}-notes`" class="block text-sm font-medium text-n-slate-12 mb-1">
        Anotações
        <span v-if="isRequired('notes')" class="text-n-ruby-9">*</span>
      </label>
      <textarea :id="`${formId}-notes`" :aria-required="isRequired('notes')" :aria-invalid="!!errors.notes" v-model="form.notes" rows="3"
                class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12 resize-none"
                placeholder="Observações sobre a ligação (opcional)" />
      <p v-if="errors.notes" class="text-xs text-n-ruby-9 mt-1">{{ errors.notes }}</p>
    </div>

    <div v-if="isEnabled('next_contact_at')">
      <label :id="`${formId}-next_contact_at-label`" :for="`${formId}-next_contact_at`" class="block text-sm font-medium text-n-slate-12 mb-1">
        Próximo contato
        <span v-if="isRequired('next_contact_at')" class="text-n-ruby-9">*</span>
      </label>
      <VoipDateTimePicker :input-id="`${formId}-next_contact_at`" :aria-labelledby="`${formId}-next_contact_at-label`" v-model="form.next_contact_at" placeholder="Quando ligar de novo" />
    </div>

    <div v-if="isEnabled('appointment_date')">
      <label :id="`${formId}-appointment_date-label`" :for="`${formId}-appointment_date`" class="block text-sm font-medium text-n-slate-12 mb-1">
        Data do agendamento
        <span v-if="isRequired('appointment_date')" class="text-n-ruby-9">*</span>
      </label>
      <VoipDateTimePicker :input-id="`${formId}-appointment_date`" :aria-labelledby="`${formId}-appointment_date-label`" v-model="form.appointment_date" placeholder="Data/hora da consulta ou reunião" />
    </div>

    <!-- Phase 2.9 (P1): temperatura do lead e preenchida so pela IA (via lead scoring).
         Agente nao escolhe mais manualmente. Mantemos a coluna no DB, so escondemos
         o input visual — lead_temperature continua setado pelo Voip::LeadScoringService. -->
    <div v-if="form.lead_temperature" class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11">
      🤖 Temperatura atual: <strong class="text-n-slate-12">{{ form.lead_temperature }}</strong>
      <span class="opacity-70">(preenchido automaticamente pela IA)</span>
    </div>

    <!-- Phase 2.9.10 c2: Valor do negócio -->
    <div v-if="businessValueEnabled">
      <label :for="`${formId}-business_value`" class="block text-sm font-medium text-n-slate-12 mb-1">
        💰 Valor do negócio
        <span v-if="businessValueRequired" class="text-n-ruby-9">*</span>
      </label>

      <!-- Modo livre: input numerico formatado em BRL -->
      <div v-if="businessValueMode === 'free'" class="relative">
        <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-n-slate-11 pointer-events-none">R$</span>
        <input :id="`${formId}-business_value`" :aria-required="businessValueRequired" v-model="businessValueInput" type="text" inputmode="decimal"
               placeholder="0,00"
               class="w-full rounded-xl border border-n-weak bg-n-alpha-1 pl-10 pr-3 py-2 text-n-slate-12" />
      </div>

      <!-- Modo catálogo: dropdown das Offers -->
      <div v-else-if="businessValueMode === 'offer'">
        <select :disabled="offersLoading || offersError" :aria-busy="offersLoading" :id="`${formId}-business_value`" :aria-required="businessValueRequired" :value="form.linked_offer_id ?? ''"
                class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12"
                @change="onOfferSelected($event.target.value)">
          <option value="">— Selecione uma oferta —</option>
          <option v-for="o in offers" :key="o.id" :value="o.id">
            {{ o.title }} · R$ {{ Number(o.value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}{{ o.currency && o.currency !== 'BRL' ? ` (${o.currency})` : '' }}
          </option>
        </select>
        <p v-if="offersLoading" role="status" class="text-sm text-n-slate-11 mt-1">
          {{ t('VOIP_DEVICES.OVERVIEW.OFFERS_LOADING') }}
        </p>
        <div v-else-if="offersError" role="alert" class="mt-2 space-y-2">
          <p class="text-sm text-n-ruby-9">{{ t('VOIP_DEVICES.OVERVIEW.OFFERS_LOAD_ERROR') }}</p>
          <button type="button" class="rounded-xl border border-n-weak px-3 py-2 text-sm text-n-slate-12" @click="loadOffers">
            {{ t('VOIP_DEVICES.OVERVIEW.RETRY_OFFERS') }}
          </button>
        </div>
        <p v-else-if="!offers.length" class="text-[11px] text-n-amber-11 mt-1">
          Nenhuma oferta cadastrada. Configure em Kanban → Ofertas.
        </p>
      </div>
    </div>

    <div v-for="field in customFields" :key="field.key">
      <label v-if="field.type !== 'bool'" :for="`${formId}-custom-${field.key}`" class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ field.label }}
        <span v-if="field.required" class="text-n-ruby-9">*</span>
      </label>
      <input v-if="['text', 'number', 'date', 'datetime'].includes(field.type)"
              :id="`${formId}-custom-${field.key}`" :aria-required="!!field.required" v-model="form.disposition_custom[field.key]"
             :type="field.type === 'datetime' ? 'datetime-local' : field.type"
             class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12" />
      <select v-else-if="field.type === 'enum'"
               :id="`${formId}-custom-${field.key}`" :aria-required="!!field.required" v-model="form.disposition_custom[field.key]"
              class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12">
        <option value="">— Selecione —</option>
        <option v-for="opt in (field.options || [])" :key="opt" :value="opt">{{ opt }}</option>
      </select>
      <label v-else-if="field.type === 'bool'" :class="{ 'min-h-11 cursor-pointer': isWorkspaceV2 }" class="inline-flex items-center gap-2">
        <input  :id="`${formId}-custom-${field.key}`" :aria-required="!!field.required" v-model="form.disposition_custom[field.key]" type="checkbox" :class="{ '!size-5': isWorkspaceV2 }" class="rounded border-n-weak" />
        <span class="text-sm text-n-slate-11">{{ field.label }}<span v-if="field.required" class="text-n-ruby-9"> *</span></span>
      </label>
      <p v-if="errors[`disposition_custom.${field.key}`]" class="text-xs text-n-ruby-9 mt-1">
        {{ errors[`disposition_custom.${field.key}`] }}
      </p>
    </div>

    <p v-if="errors.base" role="alert" class="text-sm text-n-ruby-9">{{ errors.base }}</p>

    <div class="flex items-center justify-between pt-2">
      <button v-if="showDismiss" type="button"
              class="text-sm text-n-slate-11 hover:text-n-slate-12"
              :disabled="saving"
              @click="emit('dismissed')">
        Preencher depois
      </button>
      <span v-else></span>
      <button type="button"
              class="px-4 py-2 rounded-xl bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50"
              :disabled="saving"
              @click="handleSubmit">
        {{ saving ? 'Salvando...' : (isAlreadyFilled ? 'Atualizar' : 'Salvar') }}
      </button>
    </div>
  </fieldset>
</template>
